import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, MenuItem, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography, FormControl, InputLabel, Select, Chip, Alert
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import locationService from '../../services/locationService';
import locationUserService from '../../services/locationUserService';
import Layout from '../../components/layout/Layout';
import { useSnackbar } from 'notistack';
import { getCurrentUser } from '../../services/authService';
import { formatDate } from '../../utils/dateFormatter';

const defaultForm = {
  name: '',
  email: '',
  password: '',
  role: 'USER',
  status: 'ACTIVE',
  locationId: null,
  companyId: null
};

const LocationUsersPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [formErrors, setFormErrors] = useState({});
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  const [limitDialogMessage, setLimitDialogMessage] = useState('');

  const isValidEmail = (email) => {
    if (!email) return false;
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    return pattern.test(email);
  };

  const fetchLocations = async () => {
    try {
      const res = await locationService.list({ page: 0, size: 100, sort: 'name', direction: 'ASC' });
      setLocations(res.content || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      enqueueSnackbar('Failed to load locations', { variant: 'error' });
    }
  };

  const fetchUsers = async () => {
    if (!selectedLocation || !selectedLocation.id) {
      setUsers([]);
      return;
    }

    setLoading(true);
    try {
      const users = await locationUserService.getUsersByLocation(selectedLocation.id);
      setUsers(users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load users for this location';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchUsers();
    } else {
      setUsers([]);
    }
  }, [selectedLocation]);

  const openCreate = () => {
    if (!selectedLocation) {
      enqueueSnackbar('Please select a location first', { variant: 'warning' });
      return;
    }
    const currentUser = getCurrentUser();
    const companyId = parseInt(currentUser.companyId) || 1;
    setEditing(null);
    setForm({
      ...defaultForm,
      locationId: selectedLocation.id,
      companyId: companyId
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEdit = (user) => {
    if (!user || !user.id) {
      enqueueSnackbar('Invalid user data', { variant: 'error' });
      return;
    }
    const currentUser = getCurrentUser();
    setEditing(user);
    setForm({
      name: user.name || '',
      email: user.email || '',
      password: '',
      role: user.role || 'USER',
      status: user.status || 'ACTIVE',
      locationId: user.location?.id || selectedLocation?.id,
      companyId: parseInt(currentUser?.companyId) || 1
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const errors = {};
    if (!form.name.trim()) errors.name = 'Name is required';
    if (!form.email.trim()) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(form.email.trim())) {
      errors.email = 'Invalid email format';
    }
    if (!editing && !form.password.trim()) errors.password = 'Password is required';
    if (!form.locationId) errors.locationId = 'Location is required';
    if (!form.companyId) errors.companyId = 'Company is required';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      const payload = { ...form };

      if (!payload.companyId) {
        const currentUser = getCurrentUser();
        payload.companyId = parseInt(currentUser.companyId) || 1;
      }

      payload.companyId = parseInt(payload.companyId) || 1;

      if (editing) {
        if (!payload.password) {
          delete payload.password;
        }
        await locationUserService.updateUser(editing.id, payload);
        enqueueSnackbar('User updated successfully', { variant: 'success' });
      } else {
        await locationUserService.createUser(payload);
        enqueueSnackbar('User created successfully', { variant: 'success' });
      }
      setDialogOpen(false);
      await fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);

      const errorMessage = error.response?.data?.message || 'Failed to save user';
      if (/limit|exceed|maximum|reached/i.test(String(errorMessage))) {
        setLimitDialogMessage(String(errorMessage));
        setLimitDialogOpen(true);
      } else {
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  const handleDelete = async (user) => {
    if (!user || !user.id) {
      enqueueSnackbar('Invalid user data', { variant: 'error' });
      return;
    }
    if (window.confirm(`Delete user "${user.name || 'this user'}"?`)) {
      try {
        await locationUserService.deleteUser(user.id);
        enqueueSnackbar('User deleted successfully', { variant: 'success' });
        await fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete user';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (name === 'email') {
      if (value && !isValidEmail(value)) {
        setFormErrors(prev => ({ ...prev, email: 'Invalid email format' }));
      }
    }
  };

  const getRoleChipColor = (role) => {
    switch (role) {
      case 'ADMIN': return 'primary';
      case 'MANAGER': return 'secondary';
      case 'EXECUTIVE': return 'info';
      case 'BILLING_PERSON': return 'warning';
      default: return 'default';
    }
  };

  const getStatusChipColor = (status) => {
    return status === 'ACTIVE' ? 'success' : 'error';
  };

  const content = (
    <Box p={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5">Location Users</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchUsers}
          disabled={!selectedLocation}
        >
          Refresh
        </Button>
      </Stack>

      {/* Location Selection */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Select Location</InputLabel>
                <Select
                  value={selectedLocation?.id || ''}
                  onChange={(e) => {
                    const location = locations?.find(loc => loc?.id === e.target.value);
                    setSelectedLocation(location || null);
                  }}
                  label="Select Location"
                >
                  {locations && locations.length > 0 && locations.map(location => (
                    <MenuItem key={location?.id || Math.random()} value={location?.id}>
                      {location?.name || 'Unknown'} {location?.code && `(${location.code})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              {selectedLocation && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Selected:</strong> {selectedLocation?.name || 'Unknown'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[selectedLocation?.city, selectedLocation?.state, selectedLocation?.country].filter(Boolean).join(', ') || 'No address'}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Users Table */}
      {selectedLocation && (
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6">
                Users for {selectedLocation?.name || 'Unknown Location'}
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                Add User
              </Button>
            </Stack>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users && users.length > 0 && users.map(user => (
                    <TableRow key={user?.id || Math.random()} hover>
                      <TableCell>{user?.name || '-'}</TableCell>
                      <TableCell>{user?.email || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={user?.role || 'UNKNOWN'}
                          color={getRoleChipColor(user?.role)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user?.status || 'UNKNOWN'}
                          color={getStatusChipColor(user?.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {formatDate(user?.createdAt)}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEdit(user)} disabled={!user?.id}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(user)} disabled={!user?.id}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!users || users.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        {loading ? 'Loading...' : 'No users found for this location'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* No Location Selected Message */}
      {!selectedLocation && (
        <Card>
          <CardContent>
            <Alert severity="info">
              Please select a location from the dropdown above to view and manage users for that location.
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* User Form Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                fullWidth
                required
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                fullWidth
                required
                error={!!formErrors.email}
                helperText={formErrors.email}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Password"
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                fullWidth
                required={!editing}
                error={!!formErrors.password}
                helperText={formErrors.password || (editing ? 'Leave blank to keep current password' : '')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  name="role"
                  value={form.role}
                  onChange={handleChange}
                  label="Role"
                >
                  <MenuItem value="ADMIN">Admin</MenuItem>
                  <MenuItem value="MANAGER">Manager</MenuItem>
                  <MenuItem value="EXECUTIVE">Executive</MenuItem>
                  <MenuItem value="BILLING_PERSON">Billing Person</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  label="Status"
                >
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  name="locationId"
                  value={form.locationId || ''}
                  onChange={handleChange}
                  label="Location"
                  required
                  error={!!formErrors.locationId}
                >
                  {locations.map(location => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name} {location.code && `(${location.code})`}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.locationId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {formErrors.locationId}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            {/* Hidden field for companyId */}
            <input
              type="hidden"
              name="companyId"
              value={form.companyId || ''}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={limitDialogOpen} onClose={() => setLimitDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Limit Reached</DialogTitle>
        <DialogContent>
          <Typography>{limitDialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  return (
    <Layout>
      {content}
    </Layout>
  );
};

export default LocationUsersPage;
